'use client';

import { createClient } from '@/lib/supabase/client';

export type PortalRole = 'super_admin' | 'reseller' | 'restaurant_owner';

export interface PortalUser {
  id: string;
  email: string;
  fullName: string;
  portalRole: PortalRole;
  isActive: boolean;
}

export interface ResellerProfile {
  id: string;
  portalUserId: string;
  companyName: string;
  contactEmail: string;
  contactPhone?: string;
  city?: string;
  commissionRate: number;
  isActive: boolean;
}

// PIN-based session stored in sessionStorage (no Supabase auth)
export interface PinSession {
  type: 'reseller' | 'manager';
  id: string;
  name: string;
  restaurantId?: string;
  resellerId?: string;
}

export function getPinSession(): PinSession | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem('pin_session');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function setPinSession(session: PinSession): void {
  if (typeof window === 'undefined') return;
  sessionStorage.setItem('pin_session', JSON.stringify(session));
}

export function clearPinSession(): void {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem('pin_session');
}

export async function getPortalUser(): Promise<PortalUser | null> {
  const supabase = createClient();
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data, error } = await supabase
    .from('portal_users')
    .select('*')
    .eq('id', user.id)
    .single();

  if (error || !data) return null;

  return {
    id: data.id,
    email: data.email,
    fullName: data.full_name,
    portalRole: data.portal_role as PortalRole,
    isActive: data.is_active,
  };
}

export async function getResellerProfile(portalUserId: string): Promise<ResellerProfile | null> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('resellers')
    .select('*')
    .eq('portal_user_id', portalUserId)
    .single();

  if (error || !data) return null;

  return {
    id: data.id,
    portalUserId: data.portal_user_id,
    companyName: data.company_name,
    contactEmail: data.contact_email,
    contactPhone: data.contact_phone,
    city: data.city,
    commissionRate: data.commission_rate,
    isActive: data.is_active,
  };
}

export async function signInPortal(email: string, password: string): Promise<{ user: PortalUser; reseller: ResellerProfile | null }> {
  const supabase = createClient();
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;

  const portalUser = await getPortalUser();
  if (!portalUser) throw new Error('Geen portaal toegang voor dit account.');

  let reseller: ResellerProfile | null = null;
  if (portalUser.portalRole === 'reseller') {
    reseller = await getResellerProfile(portalUser.id);
  }

  return { user: portalUser, reseller };
}

export async function signInWithPin(loginName: string, pin: string, role: 'reseller' | 'manager'): Promise<PinSession> {
  const supabase = createClient();

  if (role === 'reseller') {
    const { data, error } = await supabase.rpc('verify_reseller_pin', {
      p_login_name: loginName,
      p_pin: pin,
    });
    if (error) throw new Error('Inloggen mislukt. Controleer naam en pincode.');
    if (!data || data.length === 0) throw new Error('Naam of pincode onjuist.');
    const row = data[0];
    const session: PinSession = {
      type: 'reseller',
      id: row.reseller_id,
      name: row.company_name,
    };
    setPinSession(session);
    return session;
  } else {
    const { data, error } = await supabase.rpc('verify_manager_pin', {
      p_login_name: loginName,
      p_pin: pin,
    });
    if (error) throw new Error('Inloggen mislukt. Controleer naam en pincode.');
    if (!data || data.length === 0) throw new Error('Naam of pincode onjuist.');
    const row = data[0];
    const session: PinSession = {
      type: 'manager',
      id: row.manager_id,
      name: row.naam,
      restaurantId: row.restaurant_id,
      resellerId: row.reseller_id,
    };
    setPinSession(session);
    return session;
  }
}

export async function signOutPortal(): Promise<void> {
  clearPinSession();
  const supabase = createClient();
  await supabase.auth.signOut();
}
